/*eslint no-undef: "off"*/
import * as d3 from "d3";
import * as dc from "dc";
import * as cmk_tabs from "cmk_tabs";
import * as cmk_figures from "cmk_figures";
import * as ntop_utils from "ntop_utils";
import * as ntop_alerts from "ntop_alerts";
import * as ntop_flows from "ntop_flows";

function _add_item_link(tab_instance) {
    // Create link to ntop and hide it until we have a valid link from the backend
    tab_instance._link_item = tab_instance._tab_selection
        .append("div")
        .append("a")
        .attr("target", "_blank")
        .classed("link_to_ntop_host", true);

    tab_instance._link_item.style("display", "none");
    tab_instance._link_item.text("View data in ntopng");
}

function update_item_link(tab_instance, link) {
    tab_instance._link_item.style("display", null);
    tab_instance._link_item.attr("href", link);
}

export class HostTabs extends cmk_tabs.TabsBar {
    _get_tab_entries() {
        return [
            HostTab,
            TrafficTab,
            PacketsTab,
            PortsTab,
            PeersTab,
            ApplicationsTab,
            NtophostFlowsTab,
            NtophostEngagedAlertsTab,
            NtophostPastAlertsTab,
            NtophostFlowAlertsTab,
        ];
    }

    initialize(tab, ifid, vlanid) {
        cmk_tabs.TabsBar.prototype.initialize.call(this);
        ["engaged_alerts_tab", "past_alerts_tab", "flow_alerts_tab"].forEach(
            tab_id =>
                (this.get_tab_by_id(tab_id)._alerts_page.current_ntophost = this.current_ntophost)
        );
        d3.selectAll("." + ntop_utils.ifid_dep)
            .data()
            .forEach(o => o.set_ids(ifid, vlanid));
        this._activate_tab(this.get_tab_by_id(tab));
    }

    set_current_ntophost(value) {
        this.current_ntophost = value;
    }

    get_current_ntophost_uri_dict() {
        return {host: this.current_ntophost};
    }

    _show_error_info(error_info) {
        console.log("Error fetching data:" + error_info);
    }
}

class NtopTab extends cmk_tabs.Tab {
    constructor(tabs_bar) {
        super(tabs_bar);
        this._multi_data_fetcher = new cmk_figures.MultiDataFetcher();
        this._figures = {}; // figures from cmk
        this._dc_figures = {}; // figures from dc_library
        this._tab_selection.classed("ntop ntop_host " + ntop_utils.ifid_dep, true);
        this._ifid = tabs_bar.current_ifid;
        _add_item_link(this);
    }

    update_post_body() {
        this._multi_data_fetcher.reset();
        this._multi_data_fetcher.add_fetch_operation(this.post_url, this.get_post_body(), 120);
        this._multi_data_fetcher.subscribe_hook(this.post_url, this.get_post_body(), data =>
            this._update_data(data)
        );
    }

    set_ids(ifid, vlanid) {
        this._ifid = ifid;
        this._vlanid = vlanid;
        this.update_post_body();
    }

    get_post_body() {
        let url_params = new URLSearchParams(
            Object.assign(
                {},
                this._tabs_bar.get_current_ntophost_uri_dict(),
                this.get_current_ifid_uri_dict(),
                this.get_current_vlanid_uri_dict()
            )
        );
        return url_params.toString();
    }

    get_current_ifid_uri_dict() {
        return {ifid: this._ifid};
    }

    get_current_vlanid_uri_dict() {
        return {vlanid: this._vlanid};
    }
    activate() {
        cmk_tabs.Tab.prototype.activate.call(this);
        for (let figure_id in this._figures) {
            this._figures[figure_id].scheduler.enable();
            this._figures[figure_id].scheduler.update_if_older_than(5);
        }
        this._multi_data_fetcher.scheduler.enable();
        this._multi_data_fetcher.scheduler.update_if_older_than(5);
    }

    deactivate() {
        cmk_tabs.Tab.prototype.deactivate.call(this);
        for (let figure_id in this._figures) {
            this._figures[figure_id].scheduler.disable();
        }
        this._multi_data_fetcher.scheduler.disable();
    }
}

class HostTab extends NtopTab {
    tab_id() {
        return "host_tab";
    }

    name() {
        return "Host";
    }

    initialize() {
        let div_id = "host_data";
        this._tab_selection.append("div").attr("id", div_id);
        let table_figure_class = cmk_figures.figure_registry.get_figure("table");
        let table_figure = new table_figure_class("#" + div_id);
        this.post_url = "ajax_ntop_host_stats.py";
        table_figure.set_post_url_and_body(this.post_url, this.get_post_body());
        table_figure.scheduler.set_update_interval(60);
        table_figure.subscribe_post_render_hook(data => this._update_next_post_url(data));
        table_figure.initialize();

        this._figures["host_data"] = table_figure;
    }

    update_post_body() {
        this._figures["host_data"].set_post_url_and_body(this.post_url, this.get_post_body());
    }

    _update_next_post_url(data) {
        update_item_link(this, data["ntop_link"]);
        this._figures["host_data"].set_post_url_and_body(
            this.post_url,
            this.get_post_body() + "&_previous_data=" + JSON.stringify(data["meta"]["data"])
        );
    }
}

class TrafficTab extends NtopTab {
    tab_id() {
        return "traffic_tab";
    }

    name() {
        return "Traffic";
    }

    initialize() {
        this.post_url = "ajax_ntop_host_traffic.py";
        let post_body = this.get_post_body();
        this._multi_data_fetcher.add_fetch_operation(this.post_url, post_body, 120);
        this._multi_data_fetcher.subscribe_hook(this.post_url, post_body, data =>
            this._update_data(data)
        );

        let table_figure_class = cmk_figures.figure_registry.get_figure("table");

        // One table for the figures
        let div_id = "ntop_host_traffic_overview";
        this._tab_selection.append("div").attr("id", div_id);
        this._table_figures = new table_figure_class("#" + div_id);
        this._table_figures.initialize();

        // separate table for the traffic breakdown
        div_id = "ntop_host_traffic_breakdown";
        this._tab_selection.append("div").attr("id", div_id);
        this._table_stats = new table_figure_class("#" + div_id);
        this._table_stats.initialize();
    }

    _update_data(data) {
        this._table_figures.process_data(data["table_overview"]);
        this._table_stats.process_data(data["table_breakdown"]);

        //_update_dc_graphs_in_selection(this._table_figures._div_selection, this.tab_id());
        dc.redrawAll(this.tab_id());

        update_item_link(this, data["ntop_link"]);
    }
}

class PacketsTab extends NtopTab {
    tab_id() {
        return "packets_tab";
    }

    name() {
        return "Packets";
    }

    initialize() {
        this._tab_selection.append("div").attr("id", "table_packets");
        let table_figure_class = cmk_figures.figure_registry.get_figure("table");
        let table = new table_figure_class("#table_packets");
        table.initialize();
        this._figures["table_packets"] = table;

        this.post_url = "ajax_ntop_host_packets.py";
        let post_body = this.get_post_body();
        this._multi_data_fetcher.add_fetch_operation(this.post_url, post_body, 120);
        this._multi_data_fetcher.subscribe_hook(this.post_url, post_body, data =>
            this._update_data(data)
        );
    }

    _update_data(data) {
        if (data == undefined) return;
        this._figures["table_packets"].process_data(data["table_packets"]);

        dc.redrawAll(this.tab_id());
        update_item_link(this, data["ntop_link"]);
    }
}

class PortsTab extends NtopTab {
    tab_id() {
        return "ports_tab";
    }

    name() {
        return "Ports";
    }

    initialize() {
        let div_id = "ntop_host_ports";
        this._tab_selection.append("div").attr("id", div_id);
        let table_figure_class = cmk_figures.figure_registry.get_figure("table");
        this._table_ports = new table_figure_class("#" + div_id);
        this._table_ports.initialize();

        this.post_url = "ajax_ntop_host_ports.py";
        let post_body = this.get_post_body();
        this._multi_data_fetcher.add_fetch_operation(this.post_url, post_body, 120);
        this._multi_data_fetcher.subscribe_hook(this.post_url, post_body, data =>
            this._update_data(data)
        );
    }

    _update_data(data) {
        this._table_ports.process_data(data["table_ports"]);
        dc.redrawAll(this.tab_id());
        update_item_link(this, data["ntop_link"]);
    }
}

class PeersTab extends NtopTab {
    constructor(tabs_bar) {
        super(tabs_bar);
        this._multi_data_fetcher = new cmk_figures.MultiDataFetcher();

        // Filtered elements for the crossfilter dimension
        this._filter_dimensions = {};
        this._filters = {};
    }

    tab_id() {
        return "peers_tab";
    }

    name() {
        return "Peers";
    }

    initialize() {
        // TODO: requires cleanup. this code is still from the prototype version

        // Uses flex layout
        let div_graphs = this._tab_selection
            .append("div")
            .style("display", "flex")
            .style("flex-wrap", "wrap");

        // Create main divs
        div_graphs
            .selectAll("div.main_component")
            .data(["bar", "pie"])
            .enter()
            .append("div")
            .classed("main_component", true)
            .attr("id", d => d);

        div_graphs
            .selectAll("div#bar")
            .style("display", "flex")
            .style("flex-wrap", "wrap")
            .style("justify-content", "center")
            .style("align-content", "center")
            .style("width", "550px")
            .style("height", "400px")
            .append("div")
            .attr("id", "peers_bar");

        div_graphs
            .selectAll("div#pie")
            .style("display", "flex")
            .style("flex-wrap", "wrap")
            .style("justify-content", "center")
            .style("align-content", "center")
            .style("width", "400px")
            .style("height", "400px")
            .append("div")
            .attr("id", "peers_pie");

        this.post_url = "ajax_ntop_host_top_peers_protocols.py";
        let post_body = this.get_post_body();
        this._multi_data_fetcher.add_fetch_operation(this.post_url, post_body, 120);
        this._multi_data_fetcher.subscribe_hook(this.post_url, post_body, data =>
            this._update_data(data)
        );

        // crossfilter is globally defined
        this._crossfilter = crossfilter();

        // Host chart
        this._host_chart = new dc.RowChart("#peers_bar", this.tab_id());
        this._name_dimension = this._crossfilter.dimension(function (d) {
            return d.name;
        });
        this._traffic_per_host = this._name_dimension.group().reduceSum(function (d) {
            return +d.traffic;
        });
        this._host_chart
            .width(500)
            .height(300)
            .elasticX(true)
            .dimension(this._name_dimension)
            .group(this._traffic_per_host);
        // Tooltip
        this._host_chart.title(function (d) {
            return "Host " + d.key + ": " + ntop_utils.bytes_to_volume(d.value);
        });

        this._host_chart
            .xAxis()
            .tickFormat(function (v) {
                if (v < 1024) return v.toFixed(2);
                else return ntop_utils.bytes_to_volume(v);
            })
            .ticks(5);

        // Protocol chart
        this._pie_chart = new dc.PieChart("#peers_pie", this.tab_id());
        this._pie_dimension = this._crossfilter.dimension(d => d.l7proto);
        this._pie_group = this._pie_dimension.group().reduceSum(d => d.traffic);
        this._pie_chart
            .width(550)
            .height(300)
            .dimension(this._pie_dimension)
            .radius(350)
            .innerRadius(60)
            .externalRadiusPadding(40)
            .minAngleForLabel(0.5)
            .externalLabels(25)
            .group(this._pie_group);

        // Table
        let name_dimension = this._crossfilter.dimension(d => d.name);
        dc.renderAll(this.tab_id());

        let table_figure_class = cmk_figures.figure_registry.get_figure("dc_table");

        // Table
        let div_id = "peers_table";
        this._tab_selection.append("div").attr("id", div_id);
        this._table_stats = new table_figure_class("#" + div_id, this.tab_id());
        this._table_stats.crossfilter(this._crossfilter);
        this._table_stats.dimension(name_dimension);
        this._table_stats.columns(this._get_columns());
        this._table_stats.sort_by(d => {
            return d.name;
        });
        this._table_stats.initialize();
        this._table_stats.get_dc_chart().on("renderlet", chart => this._update_css_classes(chart));
        this._table_stats.get_dc_chart().order(d3.ascending);
    }

    _update_data(data) {
        if (data === undefined) return;

        let data_stats = data["stats"];
        if (data_stats == []) return;
        this._crossfilter.remove(() => true);
        this._crossfilter.add(data_stats);
        dc.redrawAll(this.tab_id());
        this._table_stats.remove_loading_image();

        update_item_link(this, data["ntop_link"]);
    }
    _update_css_classes(chart) {
        chart.selectAll("tr").selectAll("a").classed("ntop_link", true).attr("target", "_blank");
        chart.select("thead tr").classed("header", true);

        let columns = this._get_columns();
        chart
            .selectAll("tr")
            .selectAll("td")
            .each((d, idx, nodes) => {
                let classes = columns[idx].classes;
                if (!classes) return;
                let node = d3.select(nodes[idx]);
                classes.forEach(classname => {
                    node.classed(classname, true);
                });
            });
    }

    _get_columns() {
        return [
            {
                label: "Host",
                format: d => d.host_url,
                classes: ["host"],
            },
            {
                label: "IP Address",
                format: d => d.host,
                classes: ["host"],
            },
            {
                label: "Application",
                format: d => d.l7proto_url,
                classes: ["application"],
            },
            {
                label: "Traffic",
                format: d => d.traffic_hr,
                classes: ["traffic", "number"],
            },
            {
                // insert an empty column at the end for large screens
                label: "",
                format: () => "",
                classes: ["empty"],
            },
        ];
    }
}

class ApplicationsTab extends NtopTab {
    tab_id() {
        return "applications_tab";
    }

    name() {
        return "Apps";
    }

    initialize() {
        // overview
        let div_id = "ntop_host_apps_overview";
        this._tab_selection.append("div").attr("id", div_id);
        let table_figure_class = cmk_figures.figure_registry.get_figure("table");
        this._table = new table_figure_class("#" + div_id);
        this._table.initialize();

        // tab navigation
        this._nav = this._tab_selection
            .append("nav")
            .classed("main-navigation", true)
            .style("display", "none");
        let ul = this._nav.append("ul");
        this._li = ul
            .selectAll("li")
            .data(["Applications", "Categories"])
            .enter()
            .append("li")
            .classed("noselect", true)
            .text(d => d)
            .attr("id", d => d.toLowerCase())
            .on("click", d => {
                this._switch_subtab(d);
            });
        ul.selectAll("li#applications").classed("active", true);

        let tab_content = this._tab_selection.append("div").attr("id", "tab_content");
        this._subtab_applications = tab_content
            .append("div")
            .attr("id", "applications")
            .classed("subtab", true);
        this._subtab_categories = tab_content
            .append("div")
            .attr("id", "categories")
            .classed("subtab", true)
            .style("display", "none");

        // applications table
        div_id = "ntop_host_apps_applications";
        this._subtab_applications.append("div").attr("id", div_id);
        this._table_applications = new table_figure_class("#" + div_id);
        this._table_applications.initialize();

        // categories table
        div_id = "ntop_host_apps_categories";
        this._subtab_categories.append("div").attr("id", div_id);
        this._table_categories = new table_figure_class("#" + div_id);
        this._table_categories.initialize();

        this.post_url = "ajax_ntop_host_applications.py";
        let post_body = this.get_post_body();
        this._multi_data_fetcher.add_fetch_operation(this.post_url, post_body, 120);
        this._multi_data_fetcher.subscribe_hook(this.post_url, post_body, data => {
            this._update_data(data);
            this._update_gui();
        });
    }

    _switch_subtab(activate_id) {
        let activate_id_str = activate_id.target.id.toLowerCase();
        this._li.classed("active", d => {
            return d.toLowerCase() == activate_id_str;
        });
        this._tab_selection.selectAll("div.subtab").style("display", (d, idx, nodes) => {
            let div = d3.select(nodes[idx]);
            if (div.attr("id") == activate_id_str) return null;
            else return "none";
        });
    }

    _update_data(data) {
        this._nav.style("display", "");
        this._table.process_data(data["table_apps_overview"]);
        this._table_applications.process_data(data["table_apps_applications"]);
        this._table_categories.process_data(data["table_apps_categories"]);
        dc.redrawAll(this.tab_id());

        update_item_link(this, data["ntop_link"]);
    }

    _update_gui() {
        for (const table_obj of [this._table_applications, this._table_categories]) {
            table_obj._table.select("tr").classed("header", true);
        }
    }
}

class NtophostFlowsTab extends NtopTab {
    tab_id() {
        return "flows_tab";
    }

    name() {
        return "Flows";
    }

    initialize() {
        let div_id = "ntop_host_flows";
        this._tab_selection.append("div").attr("id", div_id);
        this._flows_dashlet = new ntop_flows.FlowsDashlet("#" + div_id, this.tab_id());
        this._flows_dashlet._default_params_dict = this._tabs_bar.get_current_ntophost_uri_dict();
        this._flows_dashlet.initialize();
        // The FlowsTab has the FlowsDashlet embedded which must also runs standalone.
        // Therefore the ifid will be injected there and we need to remove the class
        // (derived from NtopTab) here.
        this._tab_selection.classed(ntop_utils.ifid_dep, false);
    }
}

class NtophostPastAlertsTab extends ntop_alerts.PastAlertsTab {
    name() {
        return "Past alerts";
    }

    initialize() {
        ntop_alerts.PastAlertsTab.prototype.initialize.call(this);
        _initialize_alerts_tab(this);
    }
}

class NtophostEngagedAlertsTab extends ntop_alerts.EngagedAlertsTab {
    name() {
        return "Engaged alerts";
    }

    initialize() {
        ntop_alerts.EngagedAlertsTab.prototype.initialize.call(this);
        _initialize_alerts_tab(this);
        let this_reference = this;
        this._alerts_page._filter_entity = entity_val => {
            return this_reference._filter_entity(entity_val, this_reference);
        };
    }

    _filter_entity(entity_val, this_reference) {
        return entity_val.indexOf(this_reference._tabs_bar.current_ntophost) == -1;
    }
}

class NtophostFlowAlertsTab extends ntop_alerts.FlowAlertsTab {
    name() {
        return "Flow alerts";
    }

    initialize() {
        ntop_alerts.FlowAlertsTab.prototype.initialize.call(this);
        _initialize_alerts_tab(this);
    }
}

function _initialize_alerts_tab(instance) {
    let dimension = instance._alerts_page._table_details.crossfilter().dimension(d => d);
    dimension.filter(d => {
        return d.msg.indexOf(instance._tabs_bar.current_ntophost) != -1;
    });
    _add_item_link(instance);
    instance._alerts_page.subscribe_post_render_hook(data =>
        update_item_link(instance, data["ntop_link"])
    );
}
